/**
 * 1.7.0 stream C: pure helpers of the editing UI for multi-format sequences (docs/dev/formats-1.7.0-build.md): the
 * formats' UI colours, the Reframe lane's keys and geometry (where each key is on the timeline, hit-testing,
 * previous / next key, retiming within a clip), editing a clip's framing or a layer's motion "at the playhead" (the
 * key there, a new key, or the static values when the clip has no keys) with the build sheet's coalesce keys, and the
 * export list's grouping of the rows of one multi-format request.
 */
import {
  EXPORT_PRESET_IDS, PRIMARY_FORMAT, clipDuration, defaultFormatFit, formatFitOf, framingAt, framingFor, keysOf, layoutVideo, motionFor, ops, placeFor, presetAllowedForFormat, presetProblem, resolveAt, snap, ticksPerFrame, valueAt, keyCoalesce,
  type ExportDTO, type ExportPreset, type FitMode, type SequenceExportFormats, type FormatFraming, type FormatRef, type FramingKey, type KeyEase, type KeyTarget, type LayerClip, type MotionKey, type SequenceDocument, type VideoClip,
} from '@prism/contracts';

/* ---------------------------------------------------------------- colours */

/** The formats' UI colours: one palette, in formatContext.ts. */
export { formatColor } from './formatContext';

/* ---------------------------------------------------------------- clips */

export interface ClipSpan {
  clipId: string;
  /** Where the clip's visible part starts on the timeline (sequence ticks). */
  recordIn: number;
  sourceIn: number;
  sourceOut: number;
  layer: boolean;
}

/** The storyline clips and layer clips with where they sit on the timeline. */
export function clipSpans(doc: SequenceDocument): ClipSpan[] {
  const out: ClipSpan[] = layoutVideo(doc).map((p) => ({ clipId: p.clip.id, recordIn: p.recordIn, sourceIn: p.clip.sourceInTicks, sourceOut: p.clip.sourceOutTicks, layer: false }));
  for (const l of doc.layers ?? []) for (const c of l.clips) out.push({ clipId: c.id, recordIn: c.recordInTicks, sourceIn: c.sourceInTicks, sourceOut: c.sourceInTicks + clipDuration(c), layer: true });
  return out;
}

export const spanOf = (doc: SequenceDocument, clipId: string): ClipSpan | null => clipSpans(doc).find((s) => s.clipId === clipId) ?? null;

/** Where in a clip's source a sequence time is, or null outside its visible part. */
export function sourceAt(span: ClipSpan, ticks: number): number | null {
  const s = span.sourceIn + (ticks - span.recordIn);
  return s >= span.sourceIn && s < span.sourceOut ? s : null;
}

/** A clip's source time on the sequence timeline. */
export const recordOf = (span: ClipSpan, sourceTicks: number): number => span.recordIn + (sourceTicks - span.sourceIn);

export function findClip(doc: SequenceDocument, clipId: string): { clip: VideoClip | LayerClip; layer: boolean } | null {
  const v = doc.video.find((c) => c.id === clipId);
  if (v) return { clip: v, layer: false };
  for (const l of doc.layers ?? []) { const c = l.clips.find((x) => x.id === clipId); if (c) return { clip: c, layer: true }; }
  return null;
}

/** Whether a sequence shows the Reframe lane: it has formats, or any clip has framing or motion keys. */
export function showsReframeLane(doc: SequenceDocument): boolean {
  if (doc.formats?.length) return true;
  const has = (c: VideoClip | LayerClip) => Boolean(c.keys?.length || ('motion' in c && c.motion?.keys.length) || Object.values(c.formatFraming ?? {}).some((f: FormatFraming) => f.keys?.length || f.motion?.keys.length));
  return doc.video.some(has) || (doc.layers ?? []).some((l) => l.clips.some(has));
}

/* ---------------------------------------------------------------- the Reframe lane's keys */

export type LaneKeyKind = 'framing' | 'motion' | 'opacity';

export interface LaneKey {
  target: KeyTarget;
  /** The key's index in keysOf(doc, target). */
  index: number;
  kind: LaneKeyKind;
  clipId: string;
  layer: boolean;
  sourceTicks: number;
  /** Where it is on the timeline (sequence ticks). */
  recordTicks: number;
  ease: KeyEase;
}

/**
 * The keys the Reframe lane draws for a format: the framing keys of every V1 clip, and of the selected layer clip its
 * framing keys and motion keys (in a format other than the primary: its position and size keys, plus the primary's
 * opacity keys, which every format shares). Keys outside a clip's visible part are kept in the document but not listed.
 */
export function laneKeys(doc: SequenceDocument, formatId: FormatRef, selectedClipId: string | null): LaneKey[] {
  const out: LaneKey[] = [];
  const push = (span: ClipSpan, target: KeyTarget, kind: LaneKeyKind, keys: Array<FramingKey | MotionKey>, only?: (k: MotionKey) => boolean) => {
    keys.forEach((k, index) => {
      if (only && !only(k as MotionKey)) return;
      if (k.atTicks < span.sourceIn || k.atTicks >= span.sourceOut) return;
      out.push({ target, index, kind, clipId: span.clipId, layer: span.layer, sourceTicks: k.atTicks, recordTicks: recordOf(span, k.atTicks), ease: k.ease });
    });
  };
  for (const span of clipSpans(doc)) {
    if (span.layer && span.clipId !== selectedClipId) continue;
    const framing: KeyTarget = { clipId: span.clipId, formatId, kind: 'framing' };
    push(span, framing, 'framing', keysOf(doc, framing));
    if (!span.layer) continue;
    const motion: KeyTarget = { clipId: span.clipId, formatId, kind: 'motion' };
    if (formatId === PRIMARY_FORMAT) push(span, motion, 'motion', keysOf(doc, motion));
    else {
      push(span, motion, 'motion', keysOf(doc, motion));
      const primary: KeyTarget = { clipId: span.clipId, formatId: PRIMARY_FORMAT, kind: 'motion' };
      push(span, primary, 'opacity', keysOf(doc, primary), (k) => k.opacity !== undefined);
    }
  }
  return out.sort((a, b) => a.recordTicks - b.recordTicks);
}

/** The key nearest a point of the lane (pixels from its left edge) within `tolerancePx`, or null. */
export function keyAtX(keys: LaneKey[], xPx: number, pps: number, ticksPerSecond: number, tolerancePx = 6): LaneKey | null {
  let best: LaneKey | null = null;
  let bestD = Infinity;
  for (const k of keys) {
    const d = Math.abs((k.recordTicks / ticksPerSecond) * pps - xPx);
    if (d <= tolerancePx && d < bestD) { best = k; bestD = d; }
  }
  return best;
}

/** The time of the previous (-1) or next (+1) key from the playhead, more than half a frame away, or null. */
export function adjacentKeyTime(keys: Array<{ recordTicks: number }>, playhead: number, dir: -1 | 1, tpf: number): number | null {
  const times = [...new Set(keys.map((k) => k.recordTicks))].sort((a, b) => a - b);
  if (dir < 0) { for (let i = times.length - 1; i >= 0; i--) if (times[i]! < playhead - tpf / 2) return times[i]!; return null; }
  for (const t of times) if (t > playhead + tpf / 2) return t;
  return null;
}

/** A key's new source time after a drag of `deltaTicks`: on the frame grid and inside the clip's visible part. */
export function retimedSource(span: ClipSpan, fromSource: number, deltaTicks: number, rate: SequenceDocument['frameRate']): number {
  const tpf = ticksPerFrame(rate);
  const at = snap(fromSource + deltaTicks, rate);
  return Math.max(span.sourceIn, Math.min(span.sourceOut - tpf, at));
}

/* ---------------------------------------------------------------- editing at the playhead */

/** The key of `keys` at a time (source ticks), or -1. */
export const keyIndexAt = (keys: Array<{ atTicks: number }>, at: number): number => keys.findIndex((k) => k.atTicks === at);
/** Where a key at `at` goes (or is) in time order. */
export const keySlot = (keys: Array<{ atTicks: number }>, at: number): number => { const i = keyIndexAt(keys, at); return i >= 0 ? i : keys.filter((k) => k.atTicks < at).length; };

/** The ease a new key gets: the one of the key before it, else Smooth. */
const easeBefore = (keys: Array<{ atTicks: number; ease: KeyEase }>, at: number): KeyEase => [...keys].reverse().find((k) => k.atTicks < at)?.ease ?? 'smooth';

export interface FramingValues { x: number; y: number; zoom: number }

/** A clip's framing (pan and zoom) in a format at a source time. */
export function framingValuesAt(clip: VideoClip | LayerClip, formatId: FormatRef, sourceTicks: number): FramingValues {
  return framingAt(framingFor(clip, formatId), sourceTicks);
}

/**
 * The fit a clip is shown with in a format: the format's framing for its whole storyline (1.7.1) for a storyline clip,
 * else the clip's own there.
 */
export function effectiveFit(doc: Pick<SequenceDocument, 'formats' | 'fit' | 'video'>, clip: VideoClip | LayerClip, formatId: FormatRef): FitMode {
  const whole = doc.video.some((c) => c.id === clip.id) ? formatFitOf(doc, formatId) : undefined;
  return whole ?? framingFor(clip, formatId).fit ?? 'fit';
}

/** An edit and its undo coalescing key, for `apply(edit.fn, edit.coalesce)`. */
export interface Edit { fn: (d: SequenceDocument) => SequenceDocument; coalesce: string }

/**
 * Change a clip's pan or zoom in a format at a source time: with keys, the key at that time (or a new one, from the
 * values there); without keys the static framing; `forceKey` adds a key even when the clip has none (and switches the
 * format to Fill, which keys need). One drag of one value is one undo step (keyCoalesce).
 */
export function framingEdit(doc: SequenceDocument, clipId: string, formatId: FormatRef, sourceTicks: number, patch: Partial<FramingValues>, opts: { forceKey?: boolean } = {}): Edit {
  const t: KeyTarget = { clipId, formatId, kind: 'framing' };
  const at = snap(Math.max(0, sourceTicks), doc.frameRate);
  const keys = keysOf(doc, t);
  if (!keys.length && !opts.forceKey) {
    const p: { reframeX?: number; reframeY?: number; zoom?: number } = {};
    if (patch.x !== undefined) p.reframeX = patch.x;
    if (patch.y !== undefined) p.reframeY = patch.y;
    if (patch.zoom !== undefined) p.zoom = patch.zoom;
    return { fn: (d) => ops.setFormatFraming(d, clipId, formatId, p), coalesce: keyCoalesce(t, 'static') };
  }
  return {
    coalesce: keyCoalesce(t, keySlot(keys, at)),
    fn: (d) => {
      const hit = findClip(d, clipId);
      if (!hit) return d;
      let next = d;
      // Keys need Fill; a format framed Fill as a whole (1.7.1) has it already, and the clip's own fit is left as it is.
      if (effectiveFit(d, hit.clip, formatId) !== 'fill') next = ops.setFormatFraming(next, clipId, formatId, { fit: 'fill' });
      const cur = keysOf(next, t) as FramingKey[];
      const i = keyIndexAt(cur, at);
      if (i >= 0) return Object.keys(patch).length ? ops.updateKey(next, t, i, patch) : next;
      const now = framingValuesAt(findClip(next, clipId)!.clip, formatId, at);
      return ops.addKey(next, t, { atTicks: at, x: now.x, y: now.y, zoom: now.zoom, ease: easeBefore(cur, at), ...patch });
    },
  };
}

export interface MotionValues { x: number; y: number; scale: number; opacity: number }

const hasPosition = (k: MotionKey) => k.x !== undefined || k.y !== undefined || k.scale !== undefined;

/** A layer clip's position, size and opacity in a format at a source time (its motion keys, else its static values). */
export function motionValuesAt(clip: LayerClip, formatId: FormatRef, sourceTicks: number): MotionValues {
  const keys = motionFor(clip, formatId);
  const place = placeFor(clip, formatId);
  return {
    x: valueAt(keys, 'x', sourceTicks) ?? place.x,
    y: valueAt(keys, 'y', sourceTicks) ?? place.y,
    scale: valueAt(keys, 'scale', sourceTicks) ?? place.scale,
    opacity: valueAt(keys, 'opacity', sourceTicks) ?? clip.opacity,
  };
}

/** Whether a layer has position and size keys in a format / opacity keys (shared by every format). */
export const hasPositionKeys = (clip: LayerClip, formatId: FormatRef): boolean => motionFor(clip, formatId).some(hasPosition);
export const hasOpacityKeys = (clip: LayerClip): boolean => (clip.motion?.keys ?? []).some((k) => k.opacity !== undefined);

/**
 * Change a layer's position and size (x, y, scale: per format) or opacity (shared, set on the primary's keys) at a
 * source time: with keys of that kind, the key at that time or a new one (a position key always carries x, y and
 * scale); without, the static values (the primary's fields, or the format's own place). `forceKey` adds a key.
 */
export function motionEdit(doc: SequenceDocument, clipId: string, formatId: FormatRef, sourceTicks: number, patch: Partial<MotionValues>, opts: { forceKey?: boolean } = {}): Edit {
  const at = snap(Math.max(0, sourceTicks), doc.frameRate);
  const hit = findClip(doc, clipId);
  const clip = hit?.layer ? (hit.clip as LayerClip) : null;
  const opacityOnly = patch.opacity !== undefined && patch.x === undefined && patch.y === undefined && patch.scale === undefined;
  const fid: FormatRef = opacityOnly ? PRIMARY_FORMAT : formatId;
  const t: KeyTarget = { clipId, formatId: fid, kind: 'motion' };
  if (!clip) return { fn: (d) => d, coalesce: keyCoalesce(t, 'static') };
  const keyed = opacityOnly ? hasOpacityKeys(clip) : hasPositionKeys(clip, formatId);
  if (!keyed && !opts.forceKey) {
    if (opacityOnly) return { fn: (d) => ops.updateLayerClip(d, clipId, { opacity: patch.opacity! }), coalesce: `${keyCoalesce(t, 'static')}:opacity` };
    const place: { scale?: number; x?: number; y?: number } = {};
    if (patch.scale !== undefined) place.scale = patch.scale;
    if (patch.x !== undefined) place.x = patch.x;
    if (patch.y !== undefined) place.y = patch.y;
    return {
      fn: (d) => (formatId === PRIMARY_FORMAT ? ops.updateLayerClip(d, clipId, place) : ops.setFormatFraming(d, clipId, formatId, { place })),
      coalesce: keyCoalesce(t, 'static'),
    };
  }
  const keys = keysOf(doc, t) as MotionKey[];
  return {
    coalesce: keyCoalesce(t, keySlot(keys, at)),
    fn: (d) => {
      const c = findClip(d, clipId)?.clip as LayerClip | undefined;
      if (!c) return d;
      const now = motionValuesAt(c, formatId, at);
      const cur = keysOf(d, t) as MotionKey[];
      const i = keyIndexAt(cur, at);
      const values: Partial<MotionKey> = opacityOnly ? { opacity: patch.opacity ?? now.opacity } : { x: patch.x ?? now.x, y: patch.y ?? now.y, scale: patch.scale ?? now.scale };
      if (opts.forceKey && patch.opacity !== undefined && !opacityOnly) values.opacity = patch.opacity;
      if (i >= 0) return ops.updateKey(d, t, i, values);
      return ops.addKey(d, t, { atTicks: at, ease: easeBefore(cur, at), ...values });
    },
  };
}

/**
 * What "add a key at the playhead" adds (the Reframe lane's button and R): a position key on the selected layer clip
 * when the playhead is in it, else a framing key on the V1 clip under the playhead (the selected one first; Fill is
 * switched on in the format, as keys need it). Null when there is no clip there.
 */
export function addKeyAtPlayhead(doc: SequenceDocument, formatId: FormatRef, selectedClipId: string | null, playhead: number): { edit: Edit; clipId: string; kind: 'framing' | 'motion' } | null {
  const sel = selectedClipId ? spanOf(doc, selectedClipId) : null;
  if (sel?.layer) {
    const at = sourceAt(sel, playhead);
    if (at !== null) return { edit: motionEdit(doc, sel.clipId, formatId, at, {}, { forceKey: true }), clipId: sel.clipId, kind: 'motion' };
  }
  const hit = resolveAt(doc, playhead);
  if (!hit) return null;
  const clipId = sel && !sel.layer && sourceAt(sel, playhead) !== null ? sel.clipId : hit.placed.clip.id;
  const span = spanOf(doc, clipId)!;
  return { edit: framingEdit(doc, clipId, formatId, sourceAt(span, playhead) ?? hit.sourceTicks, {}, { forceKey: true }), clipId, kind: 'framing' };
}

/* ---------------------------------------------------------------- the Format dialog */

/** Storyline clips that are Fit in a format (letterboxed there when the shapes differ). */
export const fitClipsIn = (doc: SequenceDocument, formatId: FormatRef): VideoClip[] => doc.video.filter((c) => effectiveFit(doc, c, formatId) === 'fit');

/**
 * Add a format with the framing its shape starts with (1.7.1 defaultFormatFit: Blur for 1:1 and 4:5) and, with `fill`
 * and no such framing, give every Fit storyline clip Fill in it: one document, so one undo step.
 */
export function addFormatWithFill(doc: SequenceDocument, size: { width: number; height: number; label?: string }, fill: boolean): SequenceDocument {
  const fit = defaultFormatFit(size);
  const next = ops.addFormat(doc, fit ? { ...size, fit } : size);
  if (next === doc || !fill || fit) return next;
  const id = next.formats!.at(-1)!.id;
  return fitClipsIn(next, id).reduce((d, c) => ops.setFormatFraming(d, c.id, id, { fit: 'fill' }), next);
}

/* ---------------------------------------------------------------- the export dialog */

/** The presets a format can be rendered in (1.7.0): broadcast presets (a fixed 1920×1080 frame) for 16:9 formats only, and only rates they take. */
export function presetsForFormat(f: { width: number; height: number }, frameRate: SequenceDocument['frameRate']): ExportPreset[] {
  return EXPORT_PRESET_IDS.filter((k) => presetAllowedForFormat(k, f.width, f.height) && !presetProblem(k, frameRate));
}

/** One format's line of the export dialog's checklist: on or off, and its preset. */
export interface FormatChoice { on: boolean; preset: ExportPreset }

/** The request's `formats` (1.7.0): the chosen formats in the list's order, each with its preset. */
export function exportFormatsBody(formats: Array<{ id: FormatRef }>, choices: Partial<Record<FormatRef, FormatChoice>>): SequenceExportFormats {
  return formats.filter((f) => choices[f.id]?.on).map((f) => ({ formatId: f.id, preset: choices[f.id]!.preset }));
}

/* ---------------------------------------------------------------- the export list */

export type ExportListItem =
  | { kind: 'single'; export: ExportDTO }
  | { kind: 'group'; key: string; label: string; exports: ExportDTO[] };

/**
 * The export list's items, newest first as given: the rows of one multi-format request share their snapshot
 * (sequenceVersionId), so they become one group ("Promo · 3 formats"), the primary's row first, where the request's
 * first row is; other rows stay as they are. Every other export has its own snapshot.
 */
export function groupExportRows(exports: ExportDTO[]): ExportListItem[] {
  const count = new Map<string, number>();
  for (const e of exports) if (e.sequenceVersionId) count.set(e.sequenceVersionId, (count.get(e.sequenceVersionId) ?? 0) + 1);
  const out: ExportListItem[] = [];
  const groups = new Map<string, Extract<ExportListItem, { kind: 'group' }>>();
  for (const e of exports) {
    const v = e.sequenceVersionId;
    if (!v || (count.get(v) ?? 0) < 2) { out.push({ kind: 'single', export: e }); continue; }
    let g = groups.get(v);
    if (!g) { g = { kind: 'group', key: `g-${v}`, label: '', exports: [] }; groups.set(v, g); out.push(g); }
    g.exports.push(e);
  }
  for (const g of groups.values()) {
    g.exports.sort((a, b) => (a.formatId == null ? -1 : b.formatId == null ? 1 : 0));
    g.label = `${g.exports[0]!.sequenceName} · ${g.exports.length} formats`;
  }
  return out;
}

/** What one row of a group shows: its format, preset and state ("9:16 · H.264 1080p (delivery) · Rendering…"). */
export const formatRowLabel = (e: Pick<ExportDTO, 'formatLabel' | 'formatId'>): string => e.formatLabel ?? (e.formatId ? e.formatId.replace('x', ':') : 'Primary');
